import { PropsWithChildren, Fragment } from "react";
import { usePageTitle } from "@/contexts/page-title";
import { useMatches } from "react-router";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "./breadcrumb";
import { HeadingPageTitle } from "./typography";
import { BreadcrumbHandle, useBreadcrumbs } from "@/contexts/useBreadcrumbs";

const PageHeader: React.FC<PropsWithChildren> = ({ children }) => {
    const { buttons, badges, title, additionalContent } = usePageTitle();
    const matches = useMatches();
    /**
     * Breadcrumb handles come in three flavours:
     * `breadcrumbDisplay` shows whatever string was given
     * `breadcrumbKey` shows the value for that key if it exists in the useBreadcrumbs context
     * `breadCrumbUseParam` will show the route param for the last route segment of the url
     * You can set these flavours under the `handle` key wherever the route is defined (like @/page-routes.tsx)
     */
    const breadcrumbMatches = matches.filter(
        ({ handle }) =>
            !!(handle as BreadcrumbHandle | undefined)?.breadcrumbDisplay ||
            !!(handle as BreadcrumbHandle | undefined)?.breadCrumbUseParam ||
            !!(handle as BreadcrumbHandle | undefined)?.breadcrumbKey,
    );
    const { breadcrumbs } = useBreadcrumbs();

    return (
        <section className="gap-default pb-wide pt-wider grid w-full items-start justify-self-start">
            {!!breadcrumbMatches.length && (
                <Breadcrumb>
                    <BreadcrumbList>
                        {breadcrumbMatches.map((breadcrumbMatch, index) => {
                            const isLast =
                                breadcrumbMatches.length === index + 1;
                            const breadcrumb =
                                breadcrumbMatch.handle as BreadcrumbHandle;
                            const display = breadcrumb.breadCrumbUseParam
                                ? breadcrumbMatch.pathname
                                      .split("/")
                                      .filter(Boolean)
                                      .at(-1)
                                : breadcrumb.breadcrumbDisplay
                                  ? breadcrumb.breadcrumbDisplay
                                  : !!breadcrumbs && breadcrumb.breadcrumbKey
                                    ? breadcrumbs?.[breadcrumb.breadcrumbKey]
                                    : undefined;
                            const to = breadcrumbMatch.pathname;

                            if (!display) return null;
                            return (
                                <Fragment key={display + "-" + to}>
                                    <BreadcrumbItem>
                                        {isLast ? (
                                            <BreadcrumbPage className="inline-block first-letter:uppercase">
                                                {display}
                                            </BreadcrumbPage>
                                        ) : (
                                            <BreadcrumbLink
                                                to={to}
                                                className="inline-block first-letter:uppercase"
                                            >
                                                {display}
                                            </BreadcrumbLink>
                                        )}
                                    </BreadcrumbItem>
                                    {!isLast && <BreadcrumbSeparator />}
                                </Fragment>
                            );
                        })}
                    </BreadcrumbList>
                </Breadcrumb>
            )}
            <div className="flex items-start justify-between">
                <div className="gap-narrow grid">
                    {!!badges.length && (
                        <div className="gap-narrow flex">{badges}</div>
                    )}
                    {title && <HeadingPageTitle>{title}</HeadingPageTitle>}
                </div>
                {!!buttons.length && (
                    <div className="gap-default flex items-center">
                        {buttons}
                    </div>
                )}
            </div>
            {additionalContent}
            {children}
        </section>
    );
};

export default PageHeader;
